import { useQuery } from '@tanstack/react-query';
import type { FinanciamentoContrato, IFContratoResumo } from '../types/financiamento.types';
import {
  fetchFinanciamentosAtivos,
  fetchContratosByIds,
  fetchProgressoByContratoIds,
  fetchRiscoByContratoIds,
  fetchBoletinsAprovadosByContratoIds,
  fetchAditivosAprovadosByContratoIds,
} from '../services/ifDataService';

export type { FinanciamentoContrato, IFContratoResumo };

export function useIFFinanciamentos() {
  return useQuery({
    queryKey: ['if-financiamentos'],
    queryFn: async () => {
      return (await fetchFinanciamentosAtivos()) as FinanciamentoContrato[];
    },
  });
}

export function useIFCarteiraResumo() {
  const { data: financiamentos = [], isLoading: loadingFin } = useIFFinanciamentos();

  const contratoIds = financiamentos.map(f => f.contrato_id);

  const { data: contratos = [], isLoading: loadingContratos } = useQuery({
    queryKey: ['if-contratos', contratoIds],
    queryFn: () => fetchContratosByIds(contratoIds),
    enabled: contratoIds.length > 0,
  });

  const { data: progressos = [], isLoading: loadingProg } = useQuery({
    queryKey: ['if-progressos', contratoIds],
    queryFn: () => fetchProgressoByContratoIds(contratoIds),
    enabled: contratoIds.length > 0,
  });

  const { data: riscos = [], isLoading: loadingRisk } = useQuery({
    queryKey: ['if-riscos', contratoIds],
    queryFn: () => fetchRiscoByContratoIds(contratoIds),
    enabled: contratoIds.length > 0,
  });

  const { data: boletins = [], isLoading: loadingBol } = useQuery({
    queryKey: ['if-ultimos-boletins', contratoIds],
    queryFn: () => fetchBoletinsAprovadosByContratoIds(contratoIds),
    enabled: contratoIds.length > 0,
  });

  const { data: aditivos = [], isLoading: loadingAdit } = useQuery({
    queryKey: ['if-aditivos', contratoIds],
    queryFn: () => fetchAditivosAprovadosByContratoIds(contratoIds),
    enabled: contratoIds.length > 0,
  });

  const isLoading = loadingFin || loadingContratos || loadingProg || loadingRisk || loadingBol || loadingAdit;

  const resumos: IFContratoResumo[] = contratos.map((c: any) => {
    const prog: any = progressos.find((p: any) => p.contrato_id === c.id);
    const risk: any = riscos.find((r: any) => r.contract_id === c.id);
    const lastBol: any = boletins.find((b: any) => b.contrato_id === c.id);
    const adits: any[] = aditivos.filter((a: any) => a.contrato_id === c.id);

    const valorGlobal = Number(c.valor_global) || 0;
    const valorAditivos = Number(c.valor_aditivos_acumulado) || 0;
    const bac = valorGlobal + valorAditivos;
    const ev = Number(prog?.valor_earned) || 0;

    return {
      contrato_id: c.id,
      contrato_nome: c.nome || '',
      contratada: c.contratada || '',
      cluster_id: c.cluster_id,
      valor_global: valorGlobal,
      bac,
      ev,
      start_date: c.data_inicio || '',
      end_date: c.data_fim_prevista || '',
      status_contrato: c.status || '',
      progresso_fisico_pct: Number(prog?.progresso_fisico_pct) || 0,
      progresso_ppu_pct: Number(prog?.progresso_ppu_pct) || 0,
      progresso_eap_pct: Number(prog?.progresso_eap_pct) || 0,
      progresso_ubs_pct: Number(prog?.progresso_ubs_pct) || 0,
      valor_earned: ev,
      score_risco: risk ? Number(risk.score_risco) : null,
      classificacao_risco: risk?.nivel_criticidade || null,
      tendencia: risk?.tendencia || null,
      prob_atraso: risk ? Number(risk.prob_atraso_mc) / 100 : null,
      ultimo_boletim_numero: lastBol ? Number(lastBol.numero_boletim) : null,
      ultimo_boletim_valor: lastBol ? Number(lastBol.valor_liquido) : null,
      ultimo_boletim_periodo_fim: lastBol?.periodo_fim || null,
      total_aditivos: adits.length,
    };
  });

  const progressoMedio = resumos.length > 0
    ? resumos.reduce((s, r) => s + r.progresso_fisico_pct, 0) / resumos.length
    : 0;
  const contratosEmAlerta = resumos.filter(r =>
    r.classificacao_risco === 'crítico' || r.classificacao_risco === 'critico' || r.classificacao_risco === 'alto'
  ).length;

  return {
    resumos,
    isLoading,
    kpis: {
      progressoMedio,
      contratosEmAlerta,
      totalContratos: resumos.length,
    },
  };
}
